import { useEffect, useMemo, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import {
  WITHDRAW_PASS_METHOD,
  WITHDRAW_PASS_METHOD_OPTIONS,
  formatOptionalId,
  resolveWithdrawPayoutChannel,
} from './withdrawConstants';

/**
 * 提現通過：選擇人工提款或三方提款；三方時選商戶後走三方出款流程。
 */
export default function WithdrawPassModal({ open, row, onClose, onSuccess }) {
  const [passMethod, setPassMethod] = useState('');
  const [merchantCode, setMerchantCode] = useState('');
  const [googleCode, setGoogleCode] = useState('');
  const [merchantChannels, setMerchantChannels] = useState([]);
  const [loadingChannels, setLoadingChannels] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !row) {
      return;
    }
    setPassMethod('');
    setMerchantCode('');
    setGoogleCode('');
    setError('');
    setSubmitting(false);

    let cancelled = false;
    setLoadingChannels(true);
    adminApi
      .listMerchantChannels()
      .then((list) => {
        if (!cancelled) {
          setMerchantChannels(Array.isArray(list) ? list : []);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setMerchantChannels([]);
          setError(err.message || '載入支付通道失敗');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingChannels(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [open, row?.id]);

  const payout = useMemo(
    () => resolveWithdrawPayoutChannel(merchantChannels, row),
    [merchantChannels, row],
  );

  useEffect(() => {
    if (passMethod === WITHDRAW_PASS_METHOD.THIRD_PARTY && payout.merchants.length === 1) {
      setMerchantCode(payout.merchants[0].merchantCode || '');
    }
  }, [passMethod, payout.merchants]);

  if (!open || !row) {
    return null;
  }

  const isThirdParty = passMethod === WITHDRAW_PASS_METHOD.THIRD_PARTY;

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!passMethod) {
      setError('請選擇提款方式');
      return;
    }
    const code = googleCode.trim();
    if (!code) {
      setError('請輸入谷歌驗證碼');
      return;
    }
    if (isThirdParty) {
      if (!payout.channel) {
        setError('找不到符合該提現的線上出款通道');
        return;
      }
      if (!merchantCode) {
        setError('請選擇三方商戶');
        return;
      }
    }

    setSubmitting(true);
    setError('');
    try {
      const payload = {
        id: row.id,
        action: 'CONFIRM',
        confirmStatus: 'PASS',
        googleCode: code,
      };
      if (isThirdParty) {
        payload.channelId = payout.channel.id;
        payload.merchantCode = merchantCode;
      }
      await adminApi.withdrawAudit(payload);
      onSuccess?.();
      onClose?.();
    } catch (err) {
      setError(err.message || '審核失敗');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="admin-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="admin-modal"
        role="dialog"
        aria-modal="true"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="admin-modal__header">
          <h3>通过：提款方式确认</h3>
          <button type="button" className="admin-modal__close" onClick={onClose}>
            ×
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="admin-modal__body">
            <p className="page-desc">
              提現單號：
              <span className="mono-cell">{formatOptionalId(row.id)}</span>
            </p>

            <div className="form-field">
              <label htmlFor="withdrawPassMethod">
                提款方式
                <span className="form-required">*</span>
              </label>
              <select
                id="withdrawPassMethod"
                value={passMethod}
                onChange={(event) => {
                  setPassMethod(event.target.value);
                  setMerchantCode('');
                }}
                disabled={submitting}
              >
                <option value="">请选择提款方式</option>
                {WITHDRAW_PASS_METHOD_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            {isThirdParty ? (
              <>
                <div className="form-field">
                  <span className="form-label">支付通道</span>
                  <p className="form-static">
                    {loadingChannels
                      ? '載入中…'
                      : payout.channel
                        ? `${payout.channel.name || payout.channel.type || ''}（${payout.channel.coinPayTypeName || payout.channel.currencyType || row.currencyName || ''}）`
                        : '無可用出款通道'}
                  </p>
                </div>
                <div className="form-field">
                  <label htmlFor="withdrawPassMerchant">
                    三方商戶
                    <span className="form-required">*</span>
                  </label>
                  <select
                    id="withdrawPassMerchant"
                    value={merchantCode}
                    onChange={(event) => setMerchantCode(event.target.value)}
                    disabled={submitting || loadingChannels || !payout.merchants.length}
                  >
                    <option value="">请选择三方商戶</option>
                    {payout.merchants.map((m) => (
                      <option key={m.id || m.merchantCode} value={m.merchantCode}>
                        {m.name || m.merchantCode}
                      </option>
                    ))}
                  </select>
                  {!loadingChannels && payout.channel && payout.merchants.length === 0 ? (
                    <p className="page-desc">該通道暫無可用商戶</p>
                  ) : null}
                </div>
              </>
            ) : null}

            <div className="form-field">
              <label htmlFor="withdrawPassGoogleCode">
                谷歌验证码
                <span className="form-required">*</span>
              </label>
              <input
                id="withdrawPassGoogleCode"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={googleCode}
                onChange={(event) => setGoogleCode(event.target.value)}
                placeholder="请输入谷歌验证码"
                disabled={submitting}
              />
            </div>

            {error ? <div className="alert alert-error">{error}</div> : null}
          </div>
          <div className="admin-modal__footer">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={submitting}>
              關閉
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? '提交中…' : '提交'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
